/**
 * Farm & Acre Co — Hero Slideshow
 * Rotates a list of image + video slides. Auto-advance on a timer for
 * image slides; advance on the video's 'ended' event for video slides
 * (with a per-slide safety timeout in case the event never fires).
 * Muted autoplay only (browser policy); per-slide 🔊 Tap-to-listen
 * button unmutes for the rest of the session on first user gesture.
 */
(function () {
  'use strict';

  // Session-scoped sound unlock — once the user taps "tap to listen" on
  // any video slide, all future video slides start unmuted. Per-slide
  // toggle button still works to mute again.
  let sessionSoundUnlocked = false;

  function initSlideshow(root) {
    const track = root.querySelector('[data-hero-track]');
    if (!track) return;
    const slides = [...root.querySelectorAll('[data-farm-hero-slide]')];
    if (slides.length === 0) return;

    const defaultDuration = parseInt(root.dataset.defaultDuration, 10) || 5;
    const pauseOnHover = root.dataset.pauseOnHover !== 'false';
    const prevBtn = root.querySelector('[data-hero-prev]');
    const nextBtn = root.querySelector('[data-hero-next]');
    const dots = [...root.querySelectorAll('[data-hero-dot]')];

    // Filter out hidden-on-mobile slides at the live viewport size so
    // rotation skips them. Use a media query check rather than CSS
    // display:none because we still want the JS state machine to be
    // aware of the actual list of active slides.
    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    function visibleSlides() {
      return slides.filter(s => !(s.classList.contains('farm-hero-slide--desktop-only') && isMobile()));
    }

    let currentIndex = 0;
    let advanceTimer = null;
    let isPaused = false;
    let pointerInside = false;

    function attachCaptions(video) {
      // Captions URL is stored on a sibling <span data-captions-url>
      // because Shopify's video_tag filter doesn't take a captions arg.
      // Mount as <track> once on first play.
      if (video.dataset.captionsMounted === 'true') return;
      const captionsSpan = video.parentElement.querySelector('[data-captions-url]');
      if (captionsSpan) {
        const track = document.createElement('track');
        track.kind = 'subtitles';
        track.srclang = 'en';
        track.label = 'English';
        track.default = true;
        track.src = captionsSpan.dataset.captionsUrl;
        video.appendChild(track);
        video.dataset.captionsMounted = 'true';
      }
    }

    function clearAdvance() {
      if (advanceTimer) {
        clearTimeout(advanceTimer);
        advanceTimer = null;
      }
    }

    function play(slide) {
      const video = slide.querySelector('video[data-farm-hero-video]');
      if (!video) return;
      attachCaptions(video);
      // Respect session sound unlock + per-slide toggle pressed state.
      const soundBtn = slide.querySelector('[data-hero-sound-toggle]');
      const userPressedMute = soundBtn && soundBtn.dataset.userMuted === 'true';
      video.muted = userPressedMute || !sessionSoundUnlocked;
      if (soundBtn) soundBtn.setAttribute('aria-pressed', String(!video.muted));
      try {
        video.currentTime = 0;
      } catch (_) { /* iOS may throw before metadata loaded; ignore */ }
      const playPromise = video.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(() => {
          // Autoplay refused (rare since we're muted) — schedule the
          // fallback timer so rotation continues anyway.
          scheduleAdvanceForCurrent(true);
        });
      }
    }

    function stop(slide) {
      const video = slide.querySelector('video[data-farm-hero-video]');
      if (video) {
        try { video.pause(); } catch (_) {}
      }
    }

    function scheduleAdvanceForCurrent(forceTimer) {
      clearAdvance();
      if (isPaused) return;
      const slide = visibleSlides()[currentIndex];
      if (!slide) return;

      if (slide.dataset.slideType === 'video' && !forceTimer) {
        const video = slide.querySelector('video[data-farm-hero-video]');
        if (video) {
          const onEnded = () => {
            video.removeEventListener('ended', onEnded);
            if (!isPaused) goTo(currentIndex + 1);
          };
          video.addEventListener('ended', onEnded, { once: true });
          // Safety timeout: if 'ended' never fires, advance after duration
          const safetyMs = (parseInt(slide.dataset.duration, 10) || 30) * 1000;
          advanceTimer = setTimeout(() => {
            video.removeEventListener('ended', onEnded);
            goTo(currentIndex + 1);
          }, safetyMs);
          return;
        }
      }

      const ms = (parseInt(slide.dataset.duration, 10) || defaultDuration) * 1000;
      advanceTimer = setTimeout(() => goTo(currentIndex + 1), ms);
    }

    function goTo(idx) {
      const list = visibleSlides();
      if (list.length === 0) return;
      const next = ((idx % list.length) + list.length) % list.length;
      const prevSlide = list[currentIndex];
      const nextSlide = list[next];
      if (prevSlide && prevSlide !== nextSlide) {
        prevSlide.classList.remove('is-active');
        prevSlide.setAttribute('aria-hidden', 'true');
        stop(prevSlide);
      }
      nextSlide.classList.add('is-active');
      nextSlide.setAttribute('aria-hidden', 'false');
      currentIndex = next;
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === currentIndex);
        d.setAttribute('aria-selected', String(i === currentIndex));
      });
      if (nextSlide.dataset.slideType === 'video') play(nextSlide);
      scheduleAdvanceForCurrent();
    }

    function bindControls() {
      if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIndex + 1));
      dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const target = parseInt(dot.dataset.targetSlide, 10);
          if (!isNaN(target)) goTo(target);
        });
      });

      // Tap-to-listen sound toggle on each video slide
      root.querySelectorAll('[data-hero-sound-toggle]').forEach(btn => {
        btn.addEventListener('click', () => {
          const slide = btn.closest('[data-farm-hero-slide]');
          const video = slide && slide.querySelector('video[data-farm-hero-video]');
          if (!video) return;
          const nowUnmuted = video.muted;
          video.muted = !nowUnmuted;
          btn.setAttribute('aria-pressed', String(!video.muted));
          if (!video.muted) {
            sessionSoundUnlocked = true;
            btn.dataset.userMuted = 'false';
          } else {
            btn.dataset.userMuted = 'true';
          }
        });
      });

      if (pauseOnHover && window.matchMedia('(hover: hover)').matches) {
        root.addEventListener('mouseenter', () => {
          pointerInside = true;
          isPaused = true;
          clearAdvance();
        });
        root.addEventListener('mouseleave', () => {
          pointerInside = false;
          isPaused = false;
          scheduleAdvanceForCurrent();
        });
      }

      // Pause when the page is hidden; resume when it returns
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          isPaused = true;
          clearAdvance();
        } else {
          isPaused = pointerInside;
          if (!isPaused) {
            const slide = visibleSlides()[currentIndex];
            if (slide && slide.dataset.slideType === 'video') play(slide);
            scheduleAdvanceForCurrent();
          }
        }
      });
    }

    function start() {
      const list = visibleSlides();
      if (list.length === 0) return;
      // Re-anchor active slide to the first visible one if the initial
      // active is hidden-on-mobile on this viewport.
      const active = list[0];
      list.forEach((s, i) => {
        s.classList.toggle('is-active', s === active);
        s.setAttribute('aria-hidden', String(s !== active));
      });
      currentIndex = 0;
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === currentIndex);
        d.setAttribute('aria-selected', String(i === currentIndex));
      });
      if (active.dataset.slideType === 'video') play(active);
      scheduleAdvanceForCurrent();
    }

    bindControls();
    start();

    // Reset on viewport breakpoint cross (mobile ↔ desktop) so the
    // hide-on-mobile filter recalculates and we don't get stuck on a
    // now-hidden slide.
    const mql = window.matchMedia('(max-width: 749px)');
    if (mql.addEventListener) mql.addEventListener('change', () => {
      clearAdvance();
      start();
    });
  }

  function boot() {
    document.querySelectorAll('[data-farm-hero-slideshow]').forEach(initSlideshow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
